import { filter, isArray } from 'lodash';

import { DEFAULT_ZOOM_ON_CITY } from '../constants';

// Function to clean up Google Maps scripts from the DOM
const cleanupGoogleMapsScripts = () => {
  if (typeof window === 'undefined') return;

  // Remove Google Maps script tags
  const scripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
  scripts.forEach(script => script.remove());

  // Remove Google Maps CSS
  const links = document.querySelectorAll('link[href*="maps.googleapis.com"]');
  links.forEach(link => link.remove());

  // Clear Google Maps global object
  if (window.google && window.google.maps) {
    delete window.google.maps;
  }
  if (window.google) {
    delete window.google;
  }

  // Clear any Google Maps related global variables
  if (window.__googleMapsCallback) {
    delete window.__googleMapsCallback;
  }
};

const countAppliedFilters = (filters) => {
  return filter(Object.values(filters), (value) => {
    if (isArray(value) && !value.length) return false;
    return Boolean(value);
  }).length;
}

const sanitizeNumber = (value) => {
  if (value.length > 1) {
    value = value.replace(/^0+/, '');
    if (value === '') value = '0';
  }
  return value;
}

export const centerOnCity = ({ map, city, zoom = DEFAULT_ZOOM_ON_CITY }) => {
  const { lat, lng } = city.location;
  if (!lat || !lng) return;
  map?.panTo({ lat, lng });
  map?.setZoom(zoom);
}

function boundsChangedSignificantly({
  prevCenter,
  newCenter,
  prevZoom,
  newZoom,
}) {
  if (!prevCenter || !newCenter) return true;

  // Calculate the distance between centers
  const centerMovement = {
    lat: Math.abs(newCenter.lat - prevCenter.lat),
    lng: Math.abs(newCenter.lng - prevCenter.lng)
  };

  const zoomChange = Math.abs(newZoom - prevZoom);

  // Calculate zoom-dependent threshold
  // At higher zoom levels, use smaller thresholds since viewport is more sensitive
  // Base threshold decreases exponentially with zoom level
  const baseThreshold = 0.2;
  const zoomFactor = 0.7 ** Math.max(0, newZoom - 5); // Start adjusting from zoom level 5
  const dynamicCenterThreshold = baseThreshold * zoomFactor;

  const ZOOM_THRESHOLD = 1;

  return centerMovement.lat > dynamicCenterThreshold ||
    centerMovement.lng > dynamicCenterThreshold ||
    zoomChange > ZOOM_THRESHOLD;
}

const formatBounds = ({ south: minLat, north: maxLat, west: minLng, east: maxLng }) => ({ minLat, maxLat, minLng, maxLng });

const normalizeString = (str) => str && str.toLowerCase().trim();
const findCity = (cities, city, country) => {
  if (!city || !country) {
    return null;
  }

  const cityNormalized = normalizeString(city);
  const countryNormalized = normalizeString(country);

  const foundCity = cities?.find(c => {
    const cCityNormalized = normalizeString(c.city);
    const cCountryNormalized = normalizeString(c.country);
    const matches = cCityNormalized === cityNormalized && cCountryNormalized === countryNormalized;

    return matches;
  });

  return foundCity;
}

export { cleanupGoogleMapsScripts, countAppliedFilters, sanitizeNumber, boundsChangedSignificantly, formatBounds, findCity };